/**
 * HintManager.ts — Progressive tutorial hint system.
 *
 * Manages one-time tutorial hints that appear at key moments during
 * the training/origin flow. Tracks which hints have been shown to
 * avoid repetition and displays hints using the game's typewriter effect.
 */

import type { Terminal } from './Terminal';
import type { TextEffects } from './TextEffects';
import type { PanelWriter } from './PanelWriter';
import type { ColorName } from './ColorTheme';

// ─── Hint Types ─────────────────────────────────────────────

export type HintTriggerType =
    | 'first_temperament_shift'
    | 'first_skill_check'
    | 'first_stat_bonus'
    | 'first_flag_set'
    | 'epilogue';

export interface HintDefinition {
    /** Unique identifier for this hint */
    id: string;
    /** Title shown in hint header */
    title: string;
    /** Lines of explanatory text */
    lines: string[];
    /** Color for the hint border/title */
    color: ColorName;
    /** Trigger condition for when to show this hint */
    trigger: HintTriggerType;
}

export interface HintManagerOptions {
    terminal: Terminal;
    effects: TextEffects;
}

// ─── HintManager Class ──────────────────────────────────────

export class HintManager {
    private terminal: Terminal;
    private effects: TextEffects;
    private shownHints: Set<string> = new Set();
    private hintDefinitions: Map<HintTriggerType, HintDefinition> = new Map();

    constructor(options: HintManagerOptions) {
        this.terminal = options.terminal;
        this.effects = options.effects;
    }

    /**
     * Register hint definitions for lookup by trigger type.
     */
    registerHints(hints: HintDefinition[]): void {
        for (const hint of hints) {
            this.hintDefinitions.set(hint.trigger, hint);
        }
    }

    /**
     * Check if a hint should be shown for the given trigger and display it if so.
     * Returns true if a hint was shown, false otherwise.
     */
    async maybeShowHint(trigger: HintTriggerType, writer?: PanelWriter): Promise<boolean> {
        const hint = this.hintDefinitions.get(trigger);
        if (!hint) return false;

        // Don't repeat hints
        if (this.shownHints.has(hint.id)) return false;

        // Mark as shown before displaying
        this.shownHints.add(hint.id);

        // Display the hint — prefer panel-aware rendering when writer is provided
        if (writer) {
            await this.displayHintInPanel(hint, writer);
        } else {
            await this.displayHint(hint);
        }
        return true;
    }

    /**
     * Check if a specific hint has been shown.
     */
    hasShown(hintId: string): boolean {
        return this.shownHints.has(hintId);
    }

    /**
     * Mark a hint as shown without displaying it.
     * Useful for skipping hints the player has already seen in a previous session.
     */
    markShown(hintId: string): void {
        this.shownHints.add(hintId);
    }

    /**
     * Reset all hint tracking (for new game).
     */
    reset(): void {
        this.shownHints.clear();
    }

    /**
     * Get the set of shown hint IDs (for serialization).
     */
    getShownHints(): string[] {
        return Array.from(this.shownHints);
    }

    /**
     * Restore shown hints from saved state.
     */
    restoreShownHints(hintIds: string[]): void {
        this.shownHints = new Set(hintIds);
    }

    // ─── Private Methods ────────────────────────────────────────

    /**
     * Display a hint with a bordered box and typewriter effect (legacy, no panel).
     */
    private async displayHint(hint: HintDefinition): Promise<void> {
        const { terminal, effects } = this;

        terminal.blankLine();

        // Draw top border with title
        const boxWidth = 60;
        const titlePadded = ` ${hint.title} `;
        const borderLeft = '╭' + '─'.repeat(2);
        const borderRight = '─'.repeat(boxWidth - titlePadded.length - 4) + '╮';

        terminal.write('  ' + borderLeft, hint.color);
        terminal.write(titlePadded, hint.color);
        terminal.writeLine(borderRight, hint.color);

        // Display hint lines with typewriter effect
        for (const line of hint.lines) {
            terminal.write('  │ ', hint.color);
            if (line === '') {
                terminal.writeLine(' '.repeat(boxWidth - 4) + ' │', hint.color);
            } else {
                await effects.typewriter(line, 'white', 15);
                const padding = boxWidth - 4 - line.length;
                terminal.writeLine(' '.repeat(Math.max(0, padding)) + ' │', hint.color);
            }
        }

        // Draw bottom border
        terminal.writeLine('  ╰' + '─'.repeat(boxWidth - 2) + '╯', hint.color);
        terminal.blankLine();

        // Wait for acknowledgment
        await effects.pressAnyKey('Press any key to continue...');
    }

    /**
     * Display a hint within panel bounds using PanelWriter.
     * Renders the bordered box and pressAnyKey entirely through the writer.
     */
    private async displayHintInPanel(hint: HintDefinition, writer: PanelWriter): Promise<void> {
        writer.blankLine();
        await writer.renderHintBox(hint.title, hint.lines, hint.color);
        writer.blankLine();
        // No pressAnyKey — caller is responsible for the single prompt
    }
}
